import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FieldLabel } from '@shared/fields';
import media from '../../utils/mediaQueries';
import { FONT_FAMILY_MAIN } from '../../theme';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.type !== 'hidden' ? '3px' : '0'};
`;

const Input = styled.input`
  height: 48px;
  padding: 1px 16px;
  border-radius: 2px;
  border: solid 1px rgba(0, 0, 0, 0.07);
  background-color: ${(props) => props.backgroundColor};
  outline: none;
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.2px;
  color: ${(props) => props.inputTextColor};
  transition: all .2s;
  font-family: ${FONT_FAMILY_MAIN};
  &:focus {
    border-color: #23c4f8 !important;
  }
  &::placeholder {
    color: #ccd0d4; 
  }
  ${(props) => props.disabled
    ? `
    border: dashed 1px rgba(0, 0, 0, 0.07);
    color: #9aa1a9;
    &:hover {
      cursor: not-allowed;
    }
  `
    : `
    &:hover {
      border-color: rgba(41, 83, 102, 0.3);
    }
  `}
  ${(props) => props.valid === true
  && `
    border-color: #51e3ae !important;
  `}
  ${(props) => props.valid === false
  && `
    border-color: #f74656 !important;
    color: #f74656;
  `}
  width: ${(props) => (props.width ? props.width : 'auto')};
  
  ${media.mobile`
    font-size: 16px;
    
    &:focus {
      font-size: 16px;
    }
  `}
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  min-height: 20px;
  color: #f74656;
  font-family: ${FONT_FAMILY_MAIN}
`;

const ErrorEmptySpace = styled.div`
  height: 20px;
`;

const Field = forwardRef(({
  label,
  placeholder,
  type,
  disabled,
  valid,
  optionalLink,
  optionalText,
  width,
  tabIndex,
  name,
  onChange,
  onBlur,
  value,
  errorMessage,
  min,
  step,
  backgroundColor,
  inputTextColor,
  labelTextColor,
  className,
}, ref) => (
  <InputContainer className={className} type={type}>
    <FieldLabel
      label={label}
      optionalLink={optionalLink}
      optionalText={optionalText}
      valid={valid}
      labelTextColor={labelTextColor}
    />
    <Input
      width={width}
      type={type}
      tabIndex={tabIndex}
      placeholder={placeholder}
      disabled={disabled}
      valid={valid}
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      value={value}
      data-test-selector={`form-field-${name}`}
      min={min}
      step={step}
      ref={ref}
      backgroundColor={backgroundColor}
      inputTextColor={inputTextColor}
    />
    {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : type !== 'hidden' && <ErrorEmptySpace />}
  </InputContainer>
));

Field.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  optionalLink: PropTypes.string,
  optionalText: PropTypes.string,
  disabled: PropTypes.bool,
  valid: PropTypes.bool,
  width: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.any,
  errorMessage: PropTypes.string,
  tabIndex: PropTypes.string,
  min: PropTypes.number,
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  backgroundColor: PropTypes.string,
  inputTextColor: PropTypes.string,
  labelTextColor: PropTypes.string,
  className: PropTypes.string,
};
Field.defaultProps = {
  label: '',
  placeholder: '',
  type: 'text',
  disabled: false,
  valid: null,
  width: null,
  optionalLink: '',
  optionalText: '',
  name: '',
  onChange: () => {
  },
  onBlur: () => {
  },
  value: null,
  errorMessage: '',
  tabIndex: null,
  min: null,
  step: null,
  backgroundColor: '#f4f7f9',
  inputTextColor: '#354354',
  labelTextColor: '#354354',
  className: '',
};

export default Field;
